<template>
  <div>
    <loadingCube v-if="loading"/>

    <div v-else>
      <!-- Toggle: data-only vs all note types for current mask -->
      <div class="ns-controls">
        <b-checkbox v-model="onlyWithData">
          Only note types with data
        </b-checkbox>
      </div>

      <table class="ns-table">
        <thead>
        <tr>
          <th :style="zIndexReverse(3, 0)" class="">
            <div class="capsule"><span>Note type</span></div>
          </th>
          <th v-if="isWindowed" :style="zIndexReverse(3, 1)" class="rotate">
            <div class="capsule"><span>Window</span></div>
          </th>
          <th v-for="m in metricOrder" :key="m" :style="zIndexReverse(3, 2)" class="rotate">
            <div class="capsule"><span>{{ metricLabels[m] || m }}</span></div>
          </th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="(row, index) in displayRows" :key="rowKey(row) + '_' + index">
          <td>
            <UserNotesQuickLink
                mode="notes-user"
                :query="{...query, match: row.note_type_ident }"
                :params="params"
            >
              {{ noteTypeLabels[row.note_type_ident] || row.note_type_ident }}
            </UserNotesQuickLink>
          </td>
          <td v-if="isWindowed">{{ windowLabels[row.time_window] || row.time_window }}</td>
          <td v-for="m in metricOrder" :key="m">{{ toInt(row[m]) }}</td>
        </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "../../scss/shared" as *;

.ns-controls {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin: 0 0 .5rem 0;
}
</style>

<script>
// axios assumed to be provided globally.
import loadingCube from 'loaders/record_loader';
import { zIndexReverse } from 'pages/sunset/reports/notestats/components/shared/utils';
import UserNotesQuickLink from '../shared/notes_link.vue';
export default {
  name: 'sunset_note_stats_user_totals',
  components: { loadingCube, UserNotesQuickLink },
  props: {
    // From <sunset-note-stats-query-bar v-slot="{ params, meta, query }">
    params: { type: Object, required: true }, // { userId, side, start, end, windowed, mask, match, notMatch, includeOther, apiBaseUrl, ... }
    meta:   { type: Object, required: true },
    query:  { type: Object, default: () => ({}) }, // optional
  },
  data() {
    return {
      loading: true,
      rows: [],
      refreshTimer: null,
      onlyWithData: true,
      defaultApiBaseUrl: '/api/v1/',
    };
  },
  computed: {
    apiBase() {
      const base = this.params?.apiBaseUrl || this.defaultApiBaseUrl;
      return String(base).replace(/\/+$/, '');
    },

    // Merge params + query (query wins), normalize everything once
    effectiveQuery() {
      const p = this.params || {};
      const q = this.query  || {};
      const merged = { ...p, ...q };

      // resolve user id from common aliases
      const userRaw = merged.user ?? merged.userId ?? merged.user_id;
      const user = (userRaw === '' || userRaw == null) ? null : userRaw;

      return {
        user,
        side:  String((merged.side || 'combined')).toLowerCase(),
        mask:  String((merged.mask || 'all')),
        start: merged.start || '',
        end:   merged.end   || '',
        match: merged.match ?? '',
        not:   (merged.not ?? merged.notMatch ?? ''),
        includeOther: this.parseBool(merged.includeOther ?? true, true),
        windowed:     this.parseBool(merged.windowed ?? p.windowed ?? false, false),
      };
    },

    isWindowed() { return this.effectiveQuery.windowed; },

    // labels & order
    metricOrder()  { return Array.isArray(this.meta?.metrics) ? this.meta.metrics : []; },
    metricLabels() { return this.meta?.metrics_labels || {}; },
    windowLabels() { return this.meta?.window_labels || {}; },
    noteTypeLabels(){ return this.meta?.note_types?.labels || {}; },

    // Universe of applicable note type idents for current mask
    identUniverse() {
      const maskKey = String(this.effectiveQuery.mask || 'all');
      const nt = this.meta?.note_types || {};
      if (nt.by_mask && Array.isArray(nt.by_mask[maskKey])) return nt.by_mask[maskKey].map(String);
      if (Array.isArray(nt.keys)) return nt.keys.map(String);
      return Object.keys(this.noteTypeLabels);
    },

    // Universe of windows when windowed
    windowUniverse() {
      if (!this.isWindowed) return [null];
      const fromLabels = Object.keys(this.windowLabels || {});
      if (fromLabels.length) return fromLabels;
      const set = new Set(this.rows.map(r => r.time_window).filter(v => v != null));
      return set.size ? Array.from(set) : [null];
    },

    rowsMap() {
      const m = new Map();
      for (const r of this.rows) m.set(this.rowKey(r), r);
      return m;
    },

    rowsWithData() {
      return this.rows.filter(r => this.rowHasData(r));
    },

    rowsExpanded() {
      if (!this.identUniverse.length) return this.rowsWithData;
      const out = [];
      if (this.isWindowed) {
        for (const ident of this.identUniverse) {
          for (const win of this.windowUniverse) {
            const synthetic = { note_type_ident: ident, time_window: win };
            const existing = this.rowsMap.get(this.rowKey(synthetic));
            out.push(existing ? existing : this.makeZeroRow(ident, win));
          }
        }
      } else {
        for (const ident of this.identUniverse) {
          const synthetic = { note_type_ident: ident };
          const existing = this.rowsMap.get(this.rowKey(synthetic));
          out.push(existing ? existing : this.makeZeroRow(ident, null));
        }
      }
      return out;
    },

    displayRows() {
      return this.onlyWithData ? this.rowsWithData : this.rowsExpanded;
    },

    requestUrl() {
      const q = this.effectiveQuery;
      const user = q.user;
      const usp = new URLSearchParams();
      usp.set('side', q.side);
      usp.set('mask', q.mask);
      if (q.start) usp.set('start', q.start);
      if (q.end)   usp.set('end', q.end);
      usp.set('includeOther', q.includeOther ? '1' : '0');
      if (q.windowed) usp.set('windowed', '1');
      if (q.match)    usp.set('match', q.match);
      if (q.not)      usp.set('not', q.not);

      // Guard: if no user id, return a dummy url (we won't fetch)
      const userPath = (user == null || user === '') ? '' : `/${encodeURIComponent(user)}`;
      return `${this.apiBase}/sunset/reports/note-stats/totals/user${userPath}?${usp.toString()}`;
    },
  },
  watch: {
    params: { handler() { this.scheduleFetch(); }, deep: true },
    query:  { handler() { this.scheduleFetch(); }, deep: true },
    // meta only affects labels/order, no refetch
  },
  async mounted() {
    await this.fetchCoreSummary();
  },
  beforeDestroy() {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
  },
  methods: {
    zIndexReverse,

    parseBool(v, fallback = false) {
      if (typeof v === 'boolean') return v;
      const s = String(v ?? '').toLowerCase();
      if (['1','true','yes','on'].includes(s)) return true;
      if (['0','false','no','off'].includes(s)) return false;
      return fallback;
    },

    toInt(v) {
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    },

    rowKey(row) {
      return this.isWindowed
          ? `${row.note_type_ident}__${row.time_window ?? 'all'}`
          : String(row.note_type_ident);
    },

    rowHasData(row) {
      for (const m of this.metricOrder) {
        const n = Number(row[m]);
        if (Number.isFinite(n) && n > 0) return true;
      }
      return false;
    },

    makeZeroRow(ident, timeWindow) {
      const r = { note_type_ident: ident };
      if (this.isWindowed) r.time_window = timeWindow;
      for (const m of this.metricOrder) r[m] = 0;
      return r;
    },

    scheduleFetch() {
      if (this.refreshTimer) clearTimeout(this.refreshTimer);
      this.refreshTimer = setTimeout(() => { this.fetchCoreSummary(); }, 120);
    },

    async fetchCoreSummary() {
      const { start, end, user } = this.effectiveQuery;
      this.loading = true;
      try {
        if (!start || !end || user == null || user === '') {
          this.rows = [];
        } else {
          const res = await axios.get(this.requestUrl);
          const payload = res && res.data ? res.data : { data: [] };
          this.rows = Array.isArray(payload.data) ? payload.data : [];
        }
      } catch (e) {
        console.error(e);
        this.rows = [];
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
